"use client";

import type { FormattedMedia } from "@frend-digital/centra/formatting";
import clsx from "clsx";
import { type SetStateAction, Suspense, useState } from "react";
import { Controller } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Skeleton from "@/components/ui/Skeleton";
import Video from "@/components/ui/Video";
import ZoomDialog from "@/components/ui/ZoomImage/ZoomDialog";
import ZoomImage from "@/components/ui/ZoomImage/ZoomImage";
import useDevice from "@/hooks/useDevice";
import { sizes } from "@/lib/utils/imageSizes";
import { useProductContext } from "../../ProductProvider";
import styles from "./media-gallery.module.css";

const SwiperGallery = ({ className }: { className?: string }) => {
  const [current, setCurrent] = useState(0);
  const { activeVariant } = useProductContext();
  const { mobile } = useDevice();
  const [zoom, setZoom] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [firstSwiper, setFirstSwiper] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [secondSwiper, setSecondSwiper] = useState<any>(null);

  let media: FormattedMedia[] = [];
  if (mobile) {
    media =
      activeVariant?.media?.map(image => ({
        ...image,
        type: image.type || "image",
      })) ?? [];
  } else {
    media =
      activeVariant?.media
        ?.filter(image => !image.isPackshot)
        .map(image => ({ ...image, type: image.type || "image" })) ?? [];
  }

  if (activeVariant?.videoUrl) {
    media.unshift({
      // @ts-expect-error - temporarily allowing video type for testing
      type: "video",
      href: activeVariant.videoUrl as string,
      alt: "Product video",
    });
  }

  return (
    <>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        scrollbar={{ draggable: true }}
        navigation
        pagination
        zoom={true}
        onSlideChange={swiper => {
          setCurrent(swiper.activeIndex);
        }}
        className={clsx(className, styles.swiper)}
        onSwiper={setFirstSwiper}
        modules={[Controller]}
        controller={{ control: secondSwiper }}
      >
        {media?.map((image, i: number) => (
          <SwiperSlide
            className={clsx(styles.slide, !zoom && styles.small)}
            key={i}
            onClick={() => setZoom(true)}
          >
            {image.type === "image" ? (
              <>
                <ZoomImage
                  setZoom={setZoom}
                  setCurrent={setCurrent}
                  current={current}
                  media={media}
                  src={image.href}
                  alt=""
                  fill
                  className={styles["image-swiper"]}
                  sizes={sizes(100, 60)}
                  loading={"eager"}
                  quality={90}
                  priority
                />
              </>
            ) : (
              <div className={styles.video}>
                <Suspense fallback={<Skeleton height="100%" />}>
                  <Video
                    url={image.href}
                    playing
                    loop
                    muted
                    width="100%"
                    height="100%"
                  />
                </Suspense>
              </div>
            )}
          </SwiperSlide>
        ))}
        {!media && (
          <SwiperSlide className={styles.slide}>
            <div className="sr-only">No image found</div>
          </SwiperSlide>
        )}
        <div className={styles.count}>
          {current + 1} / {media?.length}
        </div>
      </Swiper>
      {zoom && (
        <Suspense>
          <ZoomDialog
            setCurrent={setCurrent}
            current={current}
            media={media}
            setOpen={setZoom}
            setSecondSwiper={setSecondSwiper}
            firstSwiper={firstSwiper}
            src={""}
            alt={""}
            setZoom={(_value: SetStateAction<boolean>): void => {
              throw new Error("Function not implemented.");
            }}
          />
        </Suspense>
      )}
    </>
  );
};
export default SwiperGallery;
